import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Upload, Card, Row, Col, message, Radio, Descriptions, Typography, Image } from 'antd';
import { UploadOutlined, CheckOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FormHelper from 'helpers/FormHelper';
import { Button } from 'components';

const { Option } = Select;
const { Text } = Typography;

const WebQrForm = ({ 
  form, 
  onFinish, 
  loading, 
  updateInProcess, 
  shoppingCenters, 
  loadingCompanies,
  singleItem = {},
  isEdit = false 
}) => {
  const { t } = useTranslation();
 
  const [linkType, setLinkType] = useState('link');


  useEffect(() => {
    if (singleItem && Object.keys(singleItem).length > 0) {
      const newLinkType = singleItem.qr_link ? 'link' : singleItem.file ? 'file' : 'link';
      setLinkType(newLinkType);
    }
  }, [singleItem]);

  const handleLinkTypeChange = (e) => {
    setLinkType(e.target.value);

    if (e.target.value === 'link') {
      form.setFieldsValue({ 
        file: undefined,
        qr_link: singleItem.qr_link || '' 
      });
    } else {
      form.setFieldsValue({ 
        qr_link: '',
        file: singleItem.file ? [{ 
          uid: '-1',
          name: singleItem.file_name || 'file',
          status: 'done',
          url: typeof singleItem.file === 'string' ? singleItem.file : singleItem.file?.url || singleItem.file
        }] : undefined // Restore original file from API if exists, handle both string and object cases
      });
    }
  };


  return (
    <Card loading={loading}>
      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        initialValues={{
          shopping_center_id: null,
          qr_link: '',
          count_open: 0,
          link_type: 'link',
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='shopping_center_id'
              label={t('Outlet Village')}
              rules={[{ required: true, message: t('Seleziona un Outlet Village') }]}
            >
              <Select
                placeholder={t('Seleziona Outlet Village')}
                loading={loadingCompanies}
                allowClear
                showSearch
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {shoppingCenters
                  ?.filter((center) => center && (center.value || center.id) && (center.title || center.name))
                  .map((center) => (
                    <Option key={center.value || center.id} value={center.value || center.id}>
                      {center.title || center.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='slug'
              label={t('Slug')}
              tooltip={t('Generato automaticamente dal server')}
            >
              <Input placeholder={t('Generato automaticamente')} disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name='link_type'
              label={t('Tipo di contenuto')}
            >
              <Radio.Group onChange={handleLinkTypeChange} value={linkType}>
                <Radio value='link'>{t('Link esterno')}</Radio>
                <Radio value='file'>{t('File (PDF/JPG/DOCX)')}</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {linkType === 'link' ? (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name='qr_link'
                label={t('Link')}
                rules={[
                  { required: linkType === 'link', message: t('Inserisci un link valido') },
                  { type: 'url', message: t('Inserisci un URL valido') }
                ]}
              >
                <Input placeholder={t('https://esempio.com')} />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name='file'
                label={t('File (PDF, JPG, DOCX)')}
                rules={[{ required: linkType === 'file', message: t('Carica un file') }]}
                valuePropName='fileList'
                getValueFromEvent={FormHelper.normFile}
                noStyle={false}
                multiple={false}
              >
                <Upload.Dragger
                  multiple={false}
                  name='file'
                  beforeUpload={FormHelper.beforeUpload}
                  accept='.pdf,.jpg,.jpeg,.docx'
                >
                  <p className='ant-upload-drag-icon'>
                    <InboxOutlined />
                  </p>
                  <p className='ant-upload-text'>
                    {t('Click or drag file to this area to upload')}
                  </p>
                </Upload.Dragger>
              </Form.Item>
              <Descriptions column={1} bordered>
                <Descriptions.Item label={`${t('Formati accettati')}:`}>
                  PDF JPG DOCX
                </Descriptions.Item>
                <Descriptions.Item label={`${t('Dimensione massima')}:`}>
                  10MB
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        )}

        {isEdit && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='count_open'
                label={t('Contatore aperture')}
              >
                <Input type='number' disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('Link generato')}
              >
                {singleItem.link ? (
                  <Text copyable>{singleItem.link}</Text>
                ) : (
                  <Text type='secondary'>{t('Link sarà generato dopo il salvataggio')}</Text>
                )}
              </Form.Item>
            </Col>
          </Row>
        )}

        {isEdit && singleItem.qr_image && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label={t('QR Code')}>
                <Image
                  src={singleItem.qr_image.url}
                  alt='QR Code'
                  width={200}
                  height={200}
                  style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col>
            <Button
              onClick={() => form.submit()}
              width={'150px'}
              big
              loading={updateInProcess}
              icon={<CheckOutlined />}
            >
              {t('Invia')}
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default WebQrForm;